import { Upload, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import OversizeFileModal from "@/components/doc/OversizeFileModal";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import { cn } from "@/lib/utils";
import type { AttachmentLabel } from "@/utils/attachmentLabels";
import { DOCUMENT_ACCEPT, DOCUMENT_ACCEPT_LABEL } from "@/utils/uploadFileTypes";
import {
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_SIZE_LABEL,
  formatFileSize,
  isCompressibleFile,
  reduceFileToLimit,
} from "@/utils/uploadSizeLimit";

export default function UploadSection({
  props,
  onSuccess,
  attachmentLabel = "WORKFILE",
  onAttachmentLabelChange,
  resolveScope,
}: {
  props: Record<string, unknown> & {
    attachedTo: string;
    attachedScope: string;
    category: string;
    folderId?: number | null;
  };
  onSuccess: () => void;
  attachmentLabel?: AttachmentLabel;
  onAttachmentLabelChange?: (v: AttachmentLabel) => void;
  /**
   * Decide the lead-or-deal grain for a drop before anything uploads. Resolves
   * to the deal marker (`null` = lead document), or to `null` for "cancelled"
   * — the owner (Attachment) answers from the user's remembered choice or by
   * opening the prompt. Omitted = upload straight away, unchanged.
   */
  resolveScope?: (
    fileCount: number,
    firstFileName: string
  ) => Promise<{ childId: number | null } | null>;
}) {
  const [dragActive, setDragActive] = useState(false);
  // Oversized images/PDFs waiting on the user's "reduce & upload" decision —
  // confirmed one at a time through OversizeFileModal.
  const [oversizeQueue, setOversizeQueue] = useState<File[]>([]);
  const [isReducing, setIsReducing] = useState(false);
  const { uploadFile } = useDocumentMutaion();

  // The grain answered for the current drop. Oversized files are confirmed
  // one at a time AFTER the question, so it has to outlive `handleFiles`.
  const scopeRef = useRef<{ childId: number | null } | null>(null);

  const uploadNow = (file: File) => {
    uploadFile.mutate(
      {
        file,
        ...props,
        attachmentLabel,
        ...(scopeRef.current ? { childId: scopeRef.current.childId } : {}),
      },
      {
        onSuccess: () => {
          onSuccess();
        },
      }
    );
  };

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const picked = Array.from(files);

    // ONE question per drop, asked before the first byte moves — asking again
    // per file would make a six-file drop unusable.
    if (resolveScope) {
      const scope = await resolveScope(picked.length, picked[0]?.name ?? "");
      if (!scope) return; // cancelled — nothing uploads
      scopeRef.current = scope;
    } else {
      scopeRef.current = null;
    }

    const oversized: File[] = [];
    picked.forEach((file) => {
      if (file.size <= MAX_UPLOAD_BYTES) {
        uploadNow(file);
      } else if (isCompressibleFile(file)) {
        oversized.push(file);
      } else {
        toast.error(
          `"${file.name}" is ${formatFileSize(file.size)} — files above ${MAX_UPLOAD_SIZE_LABEL} aren't supported, and only images and PDFs can be reduced automatically. Please upload a smaller file.`
        );
      }
    });
    if (oversized.length > 0) {
      setOversizeQueue((prev) => [...prev, ...oversized]);
    }
  };

  const currentOversize = oversizeQueue[0] ?? null;

  const handleReduceConfirm = async () => {
    if (!currentOversize || isReducing) return;
    setIsReducing(true);
    try {
      const reduced = await reduceFileToLimit(currentOversize);
      if (reduced) {
        uploadNow(reduced);
      } else {
        toast.error(
          `Couldn't reduce "${currentOversize.name}" below ${MAX_UPLOAD_SIZE_LABEL} — please upload a smaller file.`
        );
      }
    } finally {
      setIsReducing(false);
      setOversizeQueue((prev) => prev.slice(1));
    }
  };

  const handleReduceCancel = () => {
    setOversizeQueue((prev) => prev.slice(1));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    void handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Read the list before clearing the input: `handleFiles` awaits the
    // lead-or-deal answer, and `e.target.files` is emptied by the reset.
    const files = e.target.files;
    const picked = files ? Array.from(files) : [];
    e.target.value = "";
    if (picked.length === 0) return;
    void handleFiles(picked);
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-lg bg-white/55 transition-all",
          dragActive && "ring-2 ring-[#3584e4]/40 ring-offset-0 bg-[#3584e4]/[0.07]"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex min-h-10 shrink-0 flex-col gap-2 bg-[#f4f6f8]/90 px-3 py-2 sm:h-10 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-0">
          <span className="m-0 flex min-h-8 items-center text-[13px] font-medium leading-none text-[var(--color-black-8)]">
            Add files
          </span>
          <div className="flex min-h-8 w-full min-w-0 items-center gap-2 sm:w-auto">
            <span className="m-0 shrink-0 whitespace-nowrap text-[11px] font-medium leading-none text-[var(--color-black-6)]">
              Save as
            </span>
            <select
              aria-label="Document type for new uploads"
              value={attachmentLabel}
              onChange={(e) =>
                onAttachmentLabelChange?.(e.target.value as AttachmentLabel)
              }
              className="m-0 h-8 min-w-0 flex-1 cursor-pointer rounded-md border border-[#d8ddd9] bg-white px-2 text-[13px] leading-none text-[var(--color-black-9)] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1e302a]/20 sm:min-w-[8.5rem] sm:max-w-[11rem] sm:flex-none"
            >
              <option value="WORKFILE">Work file</option>
              <option value="PAYMENT_RECEIPT">Payment receipt</option>
            </select>
          </div>
        </div>

        <label
          htmlFor="file-upload"
          className={cn(
            "flex flex-col items-center justify-center gap-2 px-4 py-5 cursor-pointer transition-colors",
            dragActive ? "bg-[rgba(90,92,255,0.04)]" : "hover:bg-white/40",
            uploadFile.isPending && "opacity-50 pointer-events-none"
          )}
        >
          <div className="bg-[#f0f0ff] rounded-[8px] size-10 flex items-center justify-center">
            {uploadFile.isPending ? (
              <Loader2 className="size-6 text-[#5a5cff] animate-spin" />
            ) : (
              <Upload className="size-6 text-[#5a5cff]" strokeWidth={2} />
            )}
          </div>
          <div className="text-center space-y-1">
            <p className="text-b3-mid m-0 text-[var(--color-black-9)]">
              Drop files here or click to browse
            </p>
            <p className="text-b4 m-0 text-[var(--color-black-6)]">
              {DOCUMENT_ACCEPT_LABEL} · Max {MAX_UPLOAD_SIZE_LABEL}
            </p>
            <p className="text-[11px] m-0 text-[var(--color-black-6)]">
              Larger images & PDFs can be reduced to fit — other types must be
              re-uploaded under {MAX_UPLOAD_SIZE_LABEL}
            </p>
          </div>
          <Input
            id="file-upload"
            type="file"
            className="hidden"
            accept={DOCUMENT_ACCEPT}
            multiple
            onChange={handleFileInput}
            disabled={uploadFile.isPending}
          />
        </label>
      </div>

      {uploadFile.isPending && (
        <div className="mt-2 flex items-center gap-2 text-[12px] text-[#5a5cff]">
          <Loader2 className="size-3.5 animate-spin" />
          <span>Uploading…</span>
        </div>
      )}

      <OversizeFileModal
        file={currentOversize}
        onReduce={handleReduceConfirm}
        onCancel={handleReduceCancel}
        isReducing={isReducing}
      />
    </div>
  );
}
